<script setup lang="ts">
import { computed } from 'vue'
import SelectOptionPrime from '@/components/forms/SelectOption/SelectOptionPrime.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import type { AssetFilterOptions, AssetQueryParams } from '@/types/asset'
import { WARRANTY_STATUS_FILTER_OPTIONS, INSTALLATION_STATUS_FILTER_OPTIONS } from '@/utils/assetUtils'

// Reusable filter bar for the Installed Assets inventory. Purely presentational —
// it reads/writes an AssetQueryParams object via v-model so the parent page owns
// the actual fetch/debounce logic, and the parent page also owns the
// Clear Filters / Refresh actions (rendered in its own card header).
const props = defineProps<{
  modelValue: AssetQueryParams
  filterOptions: AssetFilterOptions
}>()

const emit = defineEmits<{
  'update:modelValue': [value: AssetQueryParams]
}>()

const update = (patch: Partial<AssetQueryParams>) => {
  emit('update:modelValue', { ...props.modelValue, ...patch })
}

const withAllOption = (label: string, values: string[]) => [
  { label, value: '' },
  ...values.map(v => ({ label: v, value: v }))
]

const customerOptions = computed(() => withAllOption('All Customers', props.filterOptions.customers))
const organizationOptions = computed(() => withAllOption('All Organizations', props.filterOptions.organizations))
const engineerOptions = computed(() => withAllOption('All Engineers', props.filterOptions.engineers))

const warrantyStatusOptions = computed(() => [
  { label: 'All Warranty Status', value: '' },
  ...WARRANTY_STATUS_FILTER_OPTIONS
])

const installationStatusOptions = computed(() => [
  { label: 'All Installation Status', value: '' },
  ...INSTALLATION_STATUS_FILTER_OPTIONS
])
</script>

<template>
  <div>
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <TextInputDefault
        id="asset-search"
        name="asset-search"
        type="text"
        size="sm"
        placeholder="Search customer, item, serial, engineer..."
        :modelValue="modelValue.search || ''"
        @update:modelValue="v => update({ search: String(v), page: 1 })"
      />

      <SelectOptionPrime
        id="asset-filter-customer"
        placeholder="Customer"
        size="sm"
        :filter="true"
        :editable="false"
        :options="customerOptions"
        :modelValue="modelValue.customerName || ''"
        @update:modelValue="v => update({ customerName: String(v || ''), page: 1 })"
      />

      <SelectOptionPrime
        id="asset-filter-organization"
        placeholder="Organization"
        size="sm"
        :filter="true"
        :editable="false"
        :options="organizationOptions"
        :modelValue="modelValue.organization || ''"
        @update:modelValue="v => update({ organization: String(v || ''), page: 1 })"
      />

      <SelectOptionPrime
        id="asset-filter-engineer"
        placeholder="Engineer"
        size="sm"
        :filter="true"
        :editable="false"
        :options="engineerOptions"
        :modelValue="modelValue.engineer || ''"
        @update:modelValue="v => update({ engineer: String(v || ''), page: 1 })"
      />

      <SelectOptionPrime
        id="asset-filter-warranty-status"
        placeholder="Warranty Status"
        size="sm"
        :options="warrantyStatusOptions"
        :modelValue="modelValue.warrantyStatus || ''"
        @update:modelValue="v => update({ warrantyStatus: (v || undefined) as AssetQueryParams['warrantyStatus'], page: 1 })"
      />

      <SelectOptionPrime
        id="asset-filter-installation-status"
        placeholder="Installation Status"
        size="sm"
        :options="installationStatusOptions"
        :modelValue="modelValue.installationStatus || ''"
        @update:modelValue="v => update({ installationStatus: (v || undefined) as AssetQueryParams['installationStatus'], page: 1 })"
      />

      <TextInputDefault
        id="asset-filter-date-from"
        name="asset-filter-date-from"
        type="date"
        size="sm"
        labelText=""
        placeholder="From date"
        :modelValue="modelValue.dateFrom || ''"
        @update:modelValue="v => update({ dateFrom: String(v), page: 1 })"
      />

      <TextInputDefault
        id="asset-filter-date-to"
        name="asset-filter-date-to"
        type="date"
        size="sm"
        labelText=""
        placeholder="To date"
        :modelValue="modelValue.dateTo || ''"
        @update:modelValue="v => update({ dateTo: String(v), page: 1 })"
      />
    </div>
  </div>
</template>
